import React, { useEffect, useRef, useState } from 'react';
import { isValidPhoneNumberInput } from '../utils/phoneNumber';

const DEFAULT_COUNTRY_PREFIX = '+420';

interface PhoneInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  invalidMessage: string;
}

export const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  invalidMessage,
  required,
  disabled,
  ...rest
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [prefixCleared, setPrefixCleared] = useState(false);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    if (!value.trim()) {
      // The displayed prefix satisfies the native `required` check, so enforce it here.
      el.setCustomValidity(required ? invalidMessage : '');
      return;
    }
    el.setCustomValidity(isValidPhoneNumberInput(value) ? '' : invalidMessage);
  }, [value, invalidMessage, required]);

  // The prefix is only displayed while the field is empty; the parent's value stays '' so an
  // untouched field is neither dirty nor submitted as a bare prefix. Once the user deletes the
  // prefix it stays deleted.
  const handleChange = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) {
      setPrefixCleared(true);
      onChange('');
    } else if (trimmed === DEFAULT_COUNTRY_PREFIX) {
      setPrefixCleared(false);
      onChange('');
    } else {
      onChange(raw);
    }
  };

  return (
    <input
      ref={inputRef}
      type="tel"
      value={value || (prefixCleared ? '' : DEFAULT_COUNTRY_PREFIX)}
      onChange={e => handleChange(e.target.value)}
      required={required}
      disabled={disabled}
      autoComplete="tel"
      {...rest}
    />
  );
};
